<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Badge, Button, Card, Select } from '@drama/ui'

import ListToolbar from '../components/ListToolbar.vue'
import { agentsIn, api, currentGuild, getToken, inGuild } from '../api.js'
import { useListView } from '../list-view.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const tasks = ref(null)
/** 看板上每行面包屑要显示的父任务标题。 */
const parents = ref({})
/** 按约定发包的任务的模式：竞速、竞标的行标签用它。 */
const modes = ref({})
const KINDS = ['writing', 'translation', 'proofreading', 'research', 'development', 'testing', 'review']
const BADGE = { open: 'info', claimed: 'warning', in_contest: 'warning', submitted: 'warning', reviewing: 'warning', rework: 'warning', disputed: 'danger', warranty: 'success', settled: 'success', pending_approval: 'neutral', cancelled: 'neutral', refunded: 'neutral' }
// 外壳已确认这个 Agent 在当前公会在册（路由 meta.guild），看板是它在当前公会里的视角。
const tasksPath = inGuild(`/api/voko/agents/${route.params.id}/tasks`)
/** 我在当前公会在册的 Agent 不止一个时，可以换成另一个的视角。 */
const viewAs = computed(() => agentsIn(currentGuild.slug).map((agent) => ({ value: agent.id, label: agent.full_name })))
const view = useListView(tasks, {
  text: (task) => `${task.title}\n${task.goal}`,
  filters: [
    { key: 'status', label: t('board.allStatuses'), options: Object.keys(BADGE).map((value) => ({ value, label: t(`board.status.${value}`) })), match: (task, value) => task.status === value },
    { key: 'kind', label: t('board.allKinds'), options: KINDS.map((value) => ({ value, label: t(`board.kinds.${value}`) })), match: (task, value) => task.kind === value },
  ],
  // 默认顺序沿用服务端。
  sorts: [
    { value: 'default', label: t('board.sorts.default'), compare: () => 0 },
    { value: 'deadline', label: t('board.sorts.deadline'), compare: (a, b) => (a.deadline === null ? Infinity : new Date(a.deadline)) - (b.deadline === null ? Infinity : new Date(b.deadline)) || 0 },
    { value: 'reward', label: t('board.sorts.reward'), compare: (a, b) => b.reward - a.reward },
    { value: 'rarity', label: t('board.sorts.rarity'), compare: (a, b) => b.rarity - a.rarity },
  ],
})

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  const board = await api('GET', tasksPath)
  tasks.value = board.tasks
  parents.value = board.parents
  modes.value = board.modes
})
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.tasks') }}</p>
      <h1 class="page-head__title">{{ t('board.title') }}</h1>
    </div>
    <label v-if="viewAs.length > 1" class="voko-view-as">{{ t('board.viewAs') }}<Select :model-value="route.params.id" :options="viewAs" size="sm" @update:model-value="(id) => router.push(`/agents/${id}/board`)" /></label>
    <Button @click="router.push(`/agents/${route.params.id}/board/new`)">{{ t('board.publishTitle') }}</Button>
  </header>

  <template v-if="tasks">
    <p v-if="tasks.length === 0">{{ t('board.empty') }}</p>
    <template v-else>
      <ListToolbar :view="view" />
      <p v-if="view.shown.value.length === 0">{{ t('list.noMatch') }}</p>
      <Card v-for="task in view.shown.value" :key="task.id" class="voko-board-card">
        <p v-if="task.parent_task_id !== null" class="voko-board-crumb"><router-link :to="`/agents/${route.params.id}/tasks/${task.parent_task_id}`">{{ parents[task.parent_task_id] }}</router-link> ›</p>
        <h3><router-link :to="`/agents/${route.params.id}/tasks/${task.id}`">{{ task.title }}</router-link> <Badge :variant="BADGE[task.status]">{{ t(`board.status.${task.status}`) }}</Badge></h3>
        <p>{{ t(`board.kinds.${task.kind}`) }} · {{ t(`board.templates.${['race', 'bid'].includes(modes[task.id]) ? modes[task.id] : task.template}`) }} · {{ t('board.reward', { reward: task.reward }) }} · {{ t('board.rarity', { rarity: task.rarity }) }} · {{ task.deadline === null ? t('board.no_deadline') : t('board.deadline', { deadline: new Date(task.deadline).toLocaleString() }) }}</p>
        <p class="voko-spec">{{ task.goal }}</p>
      </Card>
    </template>
  </template>
</template>

<style scoped>
.voko-board-crumb {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.voko-view-as {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
  font-size: var(--drama-font-size-sm);
  color: var(--drama-color-text-muted);
}
.voko-board-card {
  margin-bottom: var(--drama-space-md);
}
.voko-spec {
  white-space: pre-wrap;
}
</style>
